import {
  isNoteFailedErrorCode,
  NoteFailedClientErrorCode,
  NoteFailedSourceErrorCode,
  NoteGenerationErrorCode,
  type NoteFailedErrorCode,
} from '../../../contracts';
import { type RegenerateDocumentFailure } from '../../mutation/useMutateRegenerateDocument';

import { FatalError } from './noteGenerationErrors';

export { isNoteFailedErrorCode };
export type { NoteFailedErrorCode };

function assertNever(value: never): never {
  throw new Error(`Unhandled regenerate failure kind: ${String(value)}`);
}

export function fromFatalError(error: FatalError): NoteFailedErrorCode {
  if (error.reason && isNoteFailedErrorCode(error.reason)) return error.reason;
  if (isNoteFailedErrorCode(error.message)) return error.message;
  return NoteFailedClientErrorCode.Unknown;
}

// `handleStreamOpen` reports a non-OK SSE response as `HTTP <status>`.
const SSE_HTTP_STATUS_MESSAGE = /^HTTP (\d{3})$/;

export function fromNativeError(error: Error): NoteFailedErrorCode {
  if (error instanceof FatalError) return fromFatalError(error);
  if (isNoteFailedErrorCode(error.message)) return error.message;
  const sseStatus = SSE_HTTP_STATUS_MESSAGE.exec(error.message);
  if (sseStatus) return `HTTP_${Number(sseStatus[1])}`;
  if (
    error.name === NoteFailedClientErrorCode.TypeError ||
    error.name === NoteFailedClientErrorCode.AbortError ||
    error.name === NoteFailedClientErrorCode.NetworkError
  ) {
    return error.name;
  }
  return NoteFailedClientErrorCode.Unknown;
}

/**
 * Exhaustive on `kind` — never re-parse `failure.message`.
 * `stream-manually-aborted` is not a failure (`null`).
 */
export function fromRegenerateDocumentFailure(
  failure: RegenerateDocumentFailure
): NoteFailedErrorCode | null {
  switch (failure.kind) {
    case 'unlimited-quota-until-unset':
      return NoteGenerationErrorCode.UnlimitedQuotaUntilNotSet;
    case 'quota-exceeded':
      return NoteFailedSourceErrorCode.QuotaExceeded;
    case 'transcript-deleted':
      return NoteFailedSourceErrorCode.TranscriptDeleted;
    case 'session-not-transcribed':
      return NoteFailedSourceErrorCode.SessionNotTranscribed;
    case 'unknown':
      return NoteFailedClientErrorCode.Unknown;
    case 'stream-manually-aborted':
      return null;
    default:
      return assertNever(failure);
  }
}

/**
 * Stream `onError` / other untyped throw sites. Prefer a typed adapter when
 * the source is already classified.
 */
export function parseNoteFailedErrorCode(error: unknown): NoteFailedErrorCode {
  if (error instanceof FatalError) return fromFatalError(error);
  if (error instanceof Error) return fromNativeError(error);
  return NoteFailedClientErrorCode.Unknown;
}
